import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { UniversalFormField } from './UniversalFormField';
import { useUniversalForm } from '@/hooks/useUniversalForm';
import { FormConfig } from '@/services/FormService';
import { Loader2, Save, RotateCcw } from 'lucide-react';
import { cn } from '@/lib/utils';

interface UniversalFormProps<T> {
  formId: string;
  title?: string;
  description?: string;
  config: FormConfig;
  onSubmit: (data: T) => Promise<void>;
  initialData?: Partial<T>;
  autoSave?: boolean;
  persistState?: boolean;
  className?: string;
  showResetButton?: boolean;
}

export function UniversalForm<T extends Record<string, any>>({
  formId,
  title,
  description,
  config,
  onSubmit,
  initialData,
  autoSave = false,
  persistState = false,
  className,
  showResetButton = true,
}: UniversalFormProps<T>) {
  const {
    data,
    errors,
    isValid,
    isDirty,
    isSubmitting,
    updateField,
    submitForm,
    resetForm,
  } = useUniversalForm<T>({
    formId,
    config,
    onSubmit,
    initialData,
    autoSave,
    persistState,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    submitForm();
  };

  return (
    <Card className={cn('max-w-2xl mx-auto', className)}>
      {(title || description) && (
        <CardHeader>
          {title && <CardTitle>{title}</CardTitle>}
          {description && <CardDescription>{description}</CardDescription>}
        </CardHeader>
      )}
      
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Form Fields */}
          <div className="grid gap-4">
            {config.fields.map(field => (
              <UniversalFormField
                key={field.name}
                field={field}
                value={data[field.name]}
                error={errors[field.name]}
                onChange={(value) => updateField(field.name, value)}
                disabled={isSubmitting}
              />
            ))}
          </div>

          {/* Form Actions */}
          <div className="flex gap-3 pt-4">
            <Button
              type="submit"
              disabled={!isValid || isSubmitting}
              className="flex-1"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  {config.submitLabel || 'Submit'}
                </>
              )}
            </Button>
            
            {showResetButton && (
              <Button
                type="button"
                variant="outline"
                onClick={resetForm}
                disabled={isSubmitting || !isDirty}
              >
                <RotateCcw className="mr-2 h-4 w-4" />
                Reset
              </Button>
            )}
          </div>

          {/* Auto-save indicator */}
          {autoSave && isDirty && isValid && (
            <p className="text-sm text-muted-foreground text-center">
              Changes will be auto-saved...
            </p>
          )}
        </form>
      </CardContent>
    </Card>
  );
}